import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set default auth header if token exists
const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Auth
export const authAPI = {
  login: (data) => axios.post(`${API}/auth/login`, data),
  register: (data) => axios.post(`${API}/auth/register`, data),
  me: () => axios.get(`${API}/auth/me`),
};

// Users
export const usersAPI = {
  getAll: () => axios.get(`${API}/users`),
  update: (id, data) => axios.put(`${API}/users/${id}`, data),
  delete: (id) => axios.delete(`${API}/users/${id}`),
  changePassword: (data) => axios.post(`${API}/users/change-password`, data),
  cancelAccount: () => axios.post(`${API}/users/cancel-account`),
  getActivityLog: (params) => axios.get(`${API}/users/activity-log`, { params }),
};

// Invites (Admin only)
export const invitesAPI = {
  getAll: () => axios.get(`${API}/invites`),
  create: (data) => axios.post(`${API}/invites`, data),
  delete: (id) => axios.delete(`${API}/invites/${id}`),
  validate: (token) => axios.get(`${API}/invites/validate/${token}`),
  accept: (data) => axios.post(`${API}/invites/accept`, data),
  sendEmail: (id) => axios.post(`${API}/invites/${id}/send-email`),
};

// Contractors
export const contractorsAPI = {
  getAll: () => axios.get(`${API}/contractors`),
  getOne: (id) => axios.get(`${API}/contractors/${id}`),
  create: (data) => axios.post(`${API}/contractors`, data),
  update: (id, data) => axios.put(`${API}/contractors/${id}`, data),
  delete: (id) => axios.delete(`${API}/contractors/${id}`),
};

// Jobs
export const jobsAPI = {
  getAll: (params) => axios.get(`${API}/jobs`, { params }),
  getArchived: () => axios.get(`${API}/jobs/archived`),
  getOne: (id) => axios.get(`${API}/jobs/${id}`),
  getFull: (id) => axios.get(`${API}/jobs/${id}/full`),
  getDocuments: (id, params) => axios.get(`${API}/jobs/${id}/documents`, { params }),
  getActivity: (id) => axios.get(`${API}/jobs/${id}/activity`),
  getEmails: (id) => axios.get(`${API}/jobs/${id}/emails`),
  getMargin: (id) => axios.get(`${API}/jobs/${id}/margin`),
  create: (data) => axios.post(`${API}/jobs`, data),
  duplicate: (id) => axios.post(`${API}/jobs/${id}/duplicate`),
  importFromPo: (formData) => axios.post(`${API}/jobs/import-from-po`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => axios.put(`${API}/jobs/${id}`, data),
  updateStatus: (id, status, extra = {}) => axios.put(`${API}/jobs/${id}/status`, { status, ...extra }),
  delete: (id) => axios.delete(`${API}/jobs/${id}`),
  restore: (id) => axios.post(`${API}/jobs/${id}/restore`),
  permanentDelete: (id) => axios.delete(`${API}/jobs/${id}/permanent`),
  uploadImage: (id, formData) => axios.post(`${API}/jobs/${id}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (jobId, imageIndex) => axios.delete(`${API}/jobs/${jobId}/images/${imageIndex}`),
  sendEmail: (id, data) => axios.post(`${API}/jobs/${id}/send-email`, data),
  sendEmailWithAttachments: (id, formData) => axios.post(`${API}/jobs/${id}/send-email-attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Progress payments (per-job)
  addProgressPayment: (id, data) => axios.post(`${API}/jobs/${id}/progress-payments`, data),
  updateProgressPayment: (id, paymentId, data) => axios.patch(`${API}/jobs/${id}/progress-payments/${paymentId}`, data),
  deleteProgressPayment: (id, paymentId) => axios.delete(`${API}/jobs/${id}/progress-payments/${paymentId}`),
  getInvoicedJobs: (params) => axios.get(`${API}/invoiced-jobs`, { params }),
  // Final invoice flow
  getInvoiceSummary: (id) => axios.get(`${API}/jobs/${id}/invoice-summary`),
  generateFinalInvoice: (id, data) => axios.post(`${API}/jobs/${id}/generate-final-invoice`, data),
};

// Quotes
export const quotesAPI = {
  getAll: (params) => axios.get(`${API}/quotes`, { params }),
  getOne: (id) => axios.get(`${API}/quotes/${id}`),
  create: (data) => axios.post(`${API}/quotes`, data),
  update: (id, data) => axios.put(`${API}/quotes/${id}`, data),
  delete: (id) => axios.delete(`${API}/quotes/${id}`),
  duplicate: (id) => axios.post(`${API}/quotes/${id}/duplicate`),
  downloadPdf: (id) => axios.get(`${API}/quotes/${id}/pdf`, { responseType: 'blob' }),
  generatePO: (id, data) => axios.post(`${API}/quotes/${id}/generate-po`, data),
};

// Trade Types & Common Items
export const tradeAPI = {
  getTradeTypes: () => axios.get(`${API}/trade-types`),
  getRegions: () => axios.get(`${API}/regions`),
  getCommonPOItems: () => axios.get(`${API}/common-po-items`),
  getEmailTemplates: () => axios.get(`${API}/email-templates`),
  getQuoteSections: () => axios.get(`${API}/quote-sections`),
  getUnitTypes: () => axios.get(`${API}/unit-types`),
  addUnitType: (unit) => axios.post(`${API}/unit-types`, { unit }),
};

// Company Branding
export const brandingAPI = {
  get: () => axios.get(`${API}/branding`),
  update: (data) => axios.put(`${API}/branding`, data),
  uploadLogo: (formData) => axios.post(`${API}/branding/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Purchase Orders
export const purchaseOrdersAPI = {
  getAll: (params) => axios.get(`${API}/purchase-orders`, { params }),
  getOne: (id) => axios.get(`${API}/purchase-orders/${id}`),
  create: (data) => axios.post(`${API}/purchase-orders`, data),
  createBulk: (pos) => axios.post(`${API}/purchase-orders/bulk`, { pos }),
  update: (id, data) => axios.put(`${API}/purchase-orders/${id}`, data),
  cancel: (id, data) => axios.post(`${API}/purchase-orders/${id}/cancel`, data),
  send: (id, data) => axios.post(`${API}/purchase-orders/${id}/send`, data),
  downloadPdf: (id) => axios.get(`${API}/purchase-orders/${id}/pdf`, { responseType: 'blob' }),
};

// Pricing
export const pricingAPI = {
  getAll: (params) => axios.get(`${API}/pricing`, { params }),
  create: (data) => axios.post(`${API}/pricing`, data),
  update: (id, data) => axios.put(`${API}/pricing/${id}`, data),
  delete: (id) => axios.delete(`${API}/pricing/${id}`),
};

// Quote Templates
export const quoteTemplatesAPI = {
  getAll: () => axios.get(`${API}/quote-templates`),
  getOne: (id) => axios.get(`${API}/quote-templates/${id}`),
  create: (data) => axios.post(`${API}/quote-templates`, data),
  update: (id, data) => axios.put(`${API}/quote-templates/${id}`, data),
  delete: (id) => axios.delete(`${API}/quote-templates/${id}`),
};

// Invoices
export const invoicesAPI = {
  getAll: (params) => axios.get(`${API}/invoices`, { params }),
  getOne: (id) => axios.get(`${API}/invoices/${id}`),
  create: (data) => axios.post(`${API}/invoices`, data),
  update: (id, data) => axios.put(`${API}/invoices/${id}`, data),
  delete: (id) => axios.delete(`${API}/invoices/${id}`),
  archive: (id) => axios.post(`${API}/invoices/${id}/archive`),
  downloadPdf: (id) => axios.get(`${API}/invoices/${id}/pdf`, { responseType: 'blob' }),
};

// Schedules
export const schedulesAPI = {
  getAll: (params) => axios.get(`${API}/schedules`, { params }),
  create: (data) => axios.post(`${API}/schedules`, data),
  update: (id, data) => axios.put(`${API}/schedules/${id}`, data),
  delete: (id) => axios.delete(`${API}/schedules/${id}`),
};

// Email
export const emailAPI = {
  send: (data) => axios.post(`${API}/email/send`, data),
  getLogs: (params) => axios.get(`${API}/email/logs`, { params }),
  getDocumentsForEmail: (params) => axios.get(`${API}/documents/for-email`, { params }),
};

// Activity Logs
export const activityAPI = {
  getLogs: (params) => axios.get(`${API}/activity-logs`, { params }),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => axios.get(`${API}/dashboard/stats`),
};

// Reports
export const reportsAPI = {
  getJobsSummary: (params) => axios.get(`${API}/reports/jobs-summary`, { params }),
  getFinancial: (params) => axios.get(`${API}/reports/financial`, { params }),
  getMargins: (params) => axios.get(`${API}/reports/margins`, { params }),
  getJobsAwaitingFinalInvoice: () => axios.get(`${API}/reports/jobs-awaiting-final-invoice`),
};

// AI Services
export const aiAPI = {
  generateQuote: (data) => axios.post(`${API}/ai/generate-quote`, data),
  suggestTrades: (jobId) => axios.post(`${API}/ai/suggest-trades?job_id=${jobId}`),
  createJob: (data) => axios.post(`${API}/ai/create-job`, data),
  draftEmail: (data) => axios.post(`${API}/ai/draft-email`, data),
  getEmailTemplates: () => axios.get(`${API}/ai/email-templates`),
  estimateCost: (data) => axios.post(`${API}/ai/estimate-cost`, data),
  validateQuote: (quoteId) => axios.post(`${API}/ai/validate-quote?quote_id=${quoteId}`),
  optimizeSchedule: (data) => axios.post(`${API}/ai/optimize-schedule`, data),
  analyzeJob: (jobId) => axios.post(`${API}/ai/analyze-job?job_id=${jobId}`),
  importQuotePdf: (formData) => axios.post(`${API}/ai/import-quote-pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  importPoPdf: (formData) => axios.post(`${API}/ai/import-po-pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  importPoPdfBulk: (formData) => axios.post(`${API}/ai/import-po-pdf-bulk`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  enrichPoItems: (items) => axios.post(`${API}/ai/enrich-po-items`, { items }),
};

// Client Portal (public)
export const clientAPI = {
  getQuote: (token) => axios.get(`${API}/client/quote/${token}`),
  approveQuote: (token) => axios.post(`${API}/client/quote/${token}/approve`),
  rejectQuote: (token, reason) => axios.post(`${API}/client/quote/${token}/reject`, null, { params: { reason } }),
  downloadPdf: (token) => axios.get(`${API}/client/quote/${token}/pdf`, { responseType: 'blob' }),
  getDashboard: (token) => axios.get(`${API}/client/quote/${token}/dashboard`),
  downloadInvoicePdf: (token, invoiceId) => axios.get(`${API}/client/quote/${token}/invoice/${invoiceId}/pdf`, { responseType: 'blob' }),
  downloadDocument: (token, documentId) => axios.get(`${API}/client/quote/${token}/document/${documentId}`, { responseType: 'blob' }),
};

// Seed Data
export const seedAPI = {
  seedData: () => axios.post(`${API}/seed-data`),
};

// Documents
export const documentsAPI = {
  getAll: (params) => axios.get(`${API}/documents`, { params }),
  getOne: (id) => axios.get(`${API}/documents/${id}`),
  upload: (formData) => axios.post(`${API}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => axios.put(`${API}/documents/${id}`, data),
  delete: (id) => axios.delete(`${API}/documents/${id}`),
  getLinkedOptions: () => axios.get(`${API}/documents/linked-entities/options`),
};

export default {
  auth: authAPI,
  users: usersAPI,
  invites: invitesAPI,
  contractors: contractorsAPI,
  jobs: jobsAPI,
  quotes: quotesAPI,
  purchaseOrders: purchaseOrdersAPI,
  pricing: pricingAPI,
  invoices: invoicesAPI,
  schedules: schedulesAPI,
  email: emailAPI,
  activity: activityAPI,
  dashboard: dashboardAPI,
  reports: reportsAPI,
  ai: aiAPI,
  client: clientAPI,
  seed: seedAPI,
  documents: documentsAPI,
  branding: brandingAPI,
};
